import './styles/ManageOffer.scss';

import { Cancel, Delete, Edit, Save } from '@mui/icons-material';
import { CircularProgress, Grid, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { omit, pick } from 'lodash';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useJobOfferingQuery, useJobOfferingsMutation } from 'api/hooks/jobmarket/offers';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import Logo from 'app/components/Logo';
import { SubtleActionButton } from 'app/components/SubtleActionButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { Company, JobOffering } from 'types/AppTypes';
import { validateForm } from 'utils/FormErrorHelper';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import OfferCourses from './components/OfferCourses';
import OfferDetails from './components/OfferDetails';
import { manageOfferValidationSchema } from './validation';

interface Props {
  offerId?: number;
  closeHandler: Function;
  newOffer?: boolean;
  company?: Company;
  isEmployee?: boolean;
}

interface LocationState {
  back: string;
}

const shouldDisableType = (companyData: Company) => {
  if (
    !!companyData.country &&
    !!companyData.invoiceEmail &&
    (!!companyData.taxId || !!companyData.taxNumber)
  ) {
    return true;
  }
  return false;
};

export function ManageOffer(props: Props) {
  /**
   *
   * Initialize component data, queries, mutations, states etc.
   *
   */
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [editMode, setEditMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const currentURL = useCurrentURL();

  const { data: jobOffering, isError, isLoading } = useJobOfferingQuery(props.offerId);

  const { create, update, deletion } = useJobOfferingsMutation();

  // const jobOfferQuery = useQuery<any, any>(QUERY, {
  //   variables: { id: props.offerId },
  //   skip: props.newOffer,
  //   context: { clientName: 'federation' }
  // });

  /**
   *
   * Several Event handlers
   *
   */

  const handleCancelClick = dirty => {
    if (dirty) {
      setCancelModalOpen(true);
    } else {
      if (props.newOffer) {
        props.closeHandler();
      } else {
        setEditMode(false);
      }
    }
  };

  const disableDelete = offerData => {
    if (deletion.isPending || offerData.isActive) return true;
  };

  const handleDelete = async data => {
    if (deletion.isPending) {
      return;
    }
    try {
      await deletion.mutateAsync(data.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['companies'] });
          queryClient.invalidateQueries({ queryKey: ['jobOfferings'] });
        }
      });
      dispatch(
        AlertActions.success(t(translations.pages.manageJobOffers.alerts.deleteSuccessful()))
      );
      if (location.state) {
        const state = location.state as LocationState;
        navigate(state.back, { replace: true });
      } else {
        if (window.location.pathname !== '/jobs/manage') {
          navigate('/', { replace: true });
        }
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.manageJobOffers.alerts.deleteFailed())));
      navigate(`/jobs/${props.offerId}`, { replace: true });
    }
  };

  /**
   *
   * !Distinguish between new and existing job offers here
   *
   */
  const handleSaveClick = async (values, offerData) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    let saveBody: any = {
      ...pick(values, [
        'title',
        'startDate',
        'jobTypeId',
        'periodStart',
        'jobPriceId',
        'description',
        'externalLink'
      ]),
      courses: values.courses?.map(c => c.id)
    };
    if (values.attachement) {
      // Save logo first
      const formData = new FormData();
      formData.append('file', values.attachement);
      try {
        const { data: fileURI } = await Gateway.post('/upload/file', formData);
        saveBody.attachement = fileURI;
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.manageJobOffers.alerts.saveFailed())));
        setSubmitting(false);
        return;
      }
    }

    saveBody.jobPriceId = parseInt(saveBody.jobPriceId);
    saveBody.jobTypeId = parseInt(saveBody.jobTypeId);

    if (props.newOffer) {
      /**
       * * This is the part for new job offers
       */
      saveBody.companyId = props.company?.id;
      try {
        const data = await create.mutateAsync(saveBody, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companies', props.company?.id] });
          }
        });
        setEditMode(false);
        props.closeHandler();
        dispatch(
          AlertActions.success(t(translations.pages.manageJobOffers.alerts.saveSuccessful()))
        );
        if (data) {
          navigate(`/jobs/${data.id}`, { state: { back: currentURL } });
        }
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.manageJobOffers.alerts.saveFailed())));
      }
    } else {
      /**
       * * This is the part for existing offers
       */
      try {
        if (!props.offerId) {
          return;
        }
        await update.mutateAsync(
          { id: props.offerId, data: saveBody },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['companies'] });
            }
          }
        );
        setEditMode(false);
        props.closeHandler();
        dispatch(
          AlertActions.success(t(translations.pages.manageJobOffers.alerts.saveSuccessful()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.manageJobOffers.alerts.saveFailed())));
      }
    }
    setSubmitting(false);
  };

  const determineActions = offerData => {
    const isDisabled = disableDelete(offerData);
    return (
      <>
        <SubtleActionButton
          slide
          type="default"
          icon={Edit}
          disabled={deletion.isPending}
          onClick={() => setEditMode(true)}
        >
          {t(translations.common.edit())}
        </SubtleActionButton>
        <Tooltip
          title={t(translations.pages.jobOffers.deleteNotAllowed()) as string}
          disableHoverListener={!isDisabled}
          disableFocusListener={!isDisabled}
          disableTouchListener={!isDisabled}
        >
          <span>
            <SubtleActionButton
              slide
              type="negative"
              icon={Delete}
              disabled={isDisabled}
              loading={deletion.isPending}
              onClick={() => {
                setDeleteModalOpen(true);
              }}
            >
              {t(translations.common.delete())}
            </SubtleActionButton>
          </span>
        </Tooltip>
      </>
    );
  };

  /**
   * !Actual render method that will be wrapped by a Formik instance when in edit mode
   */
  const renderDetailView = (offerData: JobOffering, dirty?: boolean, isValid?: boolean) => {
    return (
      <div className="manage-job-offer-detail">
        <Helmet>
          <title>{!props.newOffer ? `${offerData.title} - Detailansicht` : 'New Offer'}</title>
        </Helmet>
        <Grid
          className="manage-job-offer-detail__header"
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={2}>
            {offerData.company?.logo ? (
              <Logo path="jobmarket/logos" src={offerData.company.logo} size="small" />
            ) : (
              <Logo path="jobmarket/logos" src={props.company?.logo} size="small" />
            )}
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'center' }}>
            {editMode || props.newOffer ? (
              <>
                <Tooltip
                  title={t(translations.validation.formHasErrors()) as string}
                  disableHoverListener={isValid}
                  disableFocusListener={isValid}
                  disableTouchListener={isValid}
                >
                  <span>
                    <SubtleActionButton
                      slide
                      type="positive"
                      icon={Save}
                      submit
                      disabled={!isValid || submitting}
                      loading={submitting}
                    >
                      {t(translations.common.save())}
                    </SubtleActionButton>
                  </span>
                </Tooltip>
                <SubtleActionButton
                  slide
                  type="neutral"
                  icon={Cancel}
                  disabled={submitting}
                  onClick={() => {
                    handleCancelClick(dirty);
                  }}
                >
                  {t(translations.common.cancel())}
                </SubtleActionButton>
              </>
            ) : (
              determineActions(offerData)
            )}
          </Grid>
          <Grid item xs={2} style={{ textAlign: 'end' }}>
            {!editMode && !props.newOffer && (
              <SubtleActionButton
                slide
                type="neutral"
                icon={Cancel}
                disabled={deletion.isPending}
                onClick={() => {
                  props.closeHandler();
                }}
              >
                {t(translations.common.cancel())}
              </SubtleActionButton>
            )}
          </Grid>
        </Grid>
        <div className="manage-job-offer-detail__body">
          <Grid container>
            <OfferDetails offer={offerData} editMode={editMode || props.newOffer} />
            <OfferCourses
              companyDataComplete={!!props.company ? shouldDisableType(props.company) : false}
              isEmployee={!!props.isEmployee}
              offer={offerData}
              faculties={props.company?.faculties || offerData.company.faculties}
              editMode={editMode || props.newOffer}
              selectedCourses={offerData.courses}
              newOffer={props.newOffer}
            />
          </Grid>
        </div>
        <ConfirmationDialog
          open={cancelModalOpen}
          title={t(translations.pages.manageCompanies.cancelConfirm.title())}
          onClose={() => {
            setCancelModalOpen(false);
          }}
          onConfirm={() => {
            setCancelModalOpen(false);
            if (props.newOffer) {
              props.closeHandler();
            } else {
              setEditMode(false);
            }
            dispatch(FormErrorActions.close());
          }}
        >
          <div>{t(translations.pages.manageCompanies.cancelConfirm.message())}</div>
        </ConfirmationDialog>
        <ConfirmationDialog
          open={deleteModalOpen}
          title={t(translations.pages.manageJobOffers.deleteConfirm.title())}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
          onConfirm={() => {
            setDeleteModalOpen(false);
            handleDelete(offerData);
          }}
        >
          <div>{t(translations.pages.manageJobOffers.deleteConfirm.message())}</div>
        </ConfirmationDialog>
      </div>
    );
  };

  if (isError) {
    dispatch(AlertActions.error(`${t(translations.pages.manageJobOffers.alerts.errorLoading())}`));
    navigate('/', { replace: true });
    return;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  const offerData: any = !!jobOffering
    ? {
        ...jobOffering,
        jobPriceId: jobOffering.jobPeriods?.at(-1)?.jobPrice?.id?.toString() || '',
        externalLink: jobOffering.externalLink || ''
      }
    : {
        courses: [],
        description: '',
        company: {
          faculties: []
        },
        jobPriceId: '',
        jobTypeId: 2,
        periodStart: new Date(),
        externalLink: ''
      };

  /**
   * ! Wrap render method in Formik component when in edit mode, initialize validation schema
   */
  return editMode || props.newOffer ? (
    <Formik
      isInitialValid={!props.newOffer}
      initialValues={omit({ ...offerData }, 'attachement')}
      onSubmit={values => {
        handleSaveClick(values, offerData);
      }}
      validate={values => {
        return validateForm(
          values,
          yup.object().shape(manageOfferValidationSchema(t, props)),
          dispatch
        );
      }}
    >
      {({ dirty, handleSubmit, isValid }) => {
        return <form onSubmit={handleSubmit}>{renderDetailView(offerData, dirty, isValid)}</form>;
      }}
    </Formik>
  ) : (
    <Formik initialValues={omit({ ...offerData }, 'attachement')} onSubmit={() => {}}>
      {renderDetailView(offerData)}
    </Formik>
  );
}
